import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { getSectionPOS } from 'redux/actions/Pos/sections';
import { getTablePOS } from 'redux/actions/Pos/tables';
import {
  getBusinessSettings,
  updateBusinessSettings,
} from 'redux/actions/business';
import { SET_CURRENT_BUSINESSES } from 'redux/actions/types';
import {
  computeArrangement,
  LAYOUT_TEMPLATES,
  placementFromTemplateSlot,
  resolvePresetForSlot,
} from './lib/arrangements';
import {
  clampTableSize,
  createDefaultTablePlacement,
  MAX_CANVAS_HEIGHT,
  MAX_CANVAS_WIDTH,
  MIN_CANVAS_HEIGHT,
  MIN_CANVAS_WIDTH,
  normalizeOutline,
  RECT_OUTLINE,
  resolvePresetForCapacity,
  SECTION_SHAPE_PRESETS,
  snapToGrid,
  suggestTableSizeForHall,
  TABLE_SHAPE_PRESETS,
} from './lib/defaults';
import {
  arrangeTablesInHall,
  clampPlacementInsideOutline,
  findSpotInsideOutline,
  pointInPolygon,
  projectPointInsidePolygon,
  rectCenterNormalized,
} from './lib/geometry';
import { createFloorObject, FLOOR_OBJECT_PRESETS } from './lib/objects';
import {
  allocateNextTableName,
  allocateUniqueCopyName,
} from './tableNames';
import {
  applyCatalogTruthToPlan,
  createEmptyFloorPlan,
  deepCloneFloorPlan,
  ensureSectionLayout,
  fetchCurrentFloorPlan,
  mergeFloorPlanSettingsIntoBusiness,
  readFloorPlanSettingsFromBusiness,
  resolveFloorPlan,
  saveFloorPlanToCatalog,
} from './floorPlanStorage';
import {
  readApiErrorMessage,
  syncFloorPlanCatalogWithPlan,
} from './syncFloorPlanCatalog';

/**
 * The Floor Plan builder edits ONE document (sections + tables + layout).
 * Load: GET /cloud-pos/table-visualization/current
 * Save:
 *  1) sync /cloud-pos/section + /cloud-pos/table (create/update/delete)
 *  2) PATCH /cloud-pos/table-visualization
 */
export const useFloorPlanBuilder = () => {
  const dispatch = useDispatch();
  const { currentBusiness } = useSelector(({ business }) => business);
  // `/cloud-pos/section` + `/cloud-pos/table` are the source of truth for which
  // sections/tables exist and for their names/seats. The visualization document
  // only owns layout.
  const { sections: catalogSections = [], tables: catalogTables = [] } =
    useSelector(({ pos }) => pos);

  const businessUuid = currentBusiness?.uuid || '';
  const businessId = currentBusiness?._id || '';

  const [legacyBusiness, setLegacyBusiness] = useState(currentBusiness);
  const [visualizationDoc, setVisualizationDoc] = useState(null);
  const [loadingVisualization, setLoadingVisualization] = useState(true);
  const [persistedPlan, setPersistedPlan] = useState(() =>
    createEmptyFloorPlan()
  );
  const [persistedSettings, setPersistedSettings] = useState(() =>
    readFloorPlanSettingsFromBusiness(currentBusiness)
  );
  const [plan, setPlan] = useState(() => createEmptyFloorPlan());
  const [settings, setSettings] = useState(() =>
    readFloorPlanSettingsFromBusiness(currentBusiness)
  );
  const [selectedSectionUuid, setSelectedSectionUuid] = useState(null);
  const [selectedTableUuid, setSelectedTableUuid] = useState(null);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [loadingBusiness, setLoadingBusiness] = useState(true);
  /** 'loading' | 'ready' | 'error' — only 'ready' may prune the plan. */
  const [catalogStatus, setCatalogStatus] = useState('loading');
  // Real rows the user deleted. Kept pending until Save deletes them for real.
  // "Remove from floor" is NOT a deletion — it only drops the placement.
  const [deletedTableUuids, setDeletedTableUuids] = useState([]);
  const [deletedSectionUuids, setDeletedSectionUuids] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const planRef = useRef(plan);
  const dirtyRef = useRef(false);
  /** Ignore remote echoes until they match the layout we just saved. */
  const pendingSaveJsonRef = useRef(null);
  /** True until first settled visualization/migration plan is applied. */
  const awaitingHydrationRef = useRef(true);

  useEffect(() => {
    planRef.current = plan;
  }, [plan]);

  // Load the catalog: it decides what exists, and keeps bulk add from reusing
  // T1/T2 names that already exist in /cloud-pos/table for this business.
  // 'ready' (a successful load) is required before anything is pruned.
  useEffect(() => {
    if (!businessUuid) {
      setCatalogStatus('error');
      return undefined;
    }
    let cancelled = false;
    setCatalogStatus('loading');
    Promise.all([
      dispatch(getSectionPOS(businessUuid)),
      dispatch(getTablePOS(businessUuid)),
    ])
      .then(() => {
        if (!cancelled) {
          setCatalogStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessUuid, dispatch]);

  useEffect(() => {
    let cancelled = false;
    setLoadingBusiness(true);
    getBusinessSettings()
      .then((business) => {
        if (cancelled) {
          return;
        }
        setLegacyBusiness(business);
        const nextSettings = readFloorPlanSettingsFromBusiness(business);
        setPersistedSettings(nextSettings);
        if (!settingsDirty) {
          setSettings(nextSettings);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLegacyBusiness(currentBusiness);
          setSettings(readFloorPlanSettingsFromBusiness(currentBusiness));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBusiness(false);
        }
      });
    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Load current table-visualization document (one per business).
  useEffect(() => {
    let cancelled = false;
    if (!businessUuid) {
      setVisualizationDoc(null);
      setLoadingVisualization(false);
      awaitingHydrationRef.current = false;
      return undefined;
    }
    setLoadingVisualization(true);
    awaitingHydrationRef.current = true;
    fetchCurrentFloorPlan(businessUuid)
      .then(({ doc }) => {
        if (!cancelled) {
          setVisualizationDoc(doc);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVisualizationDoc(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingVisualization(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessUuid]);

  // Prefer visualization; fall back to legacy business metadata only. The plan
  // is then reconciled against the catalog, which owns existence and identity.
  useEffect(() => {
    if (loadingBusiness || loadingVisualization || catalogStatus === 'loading') {
      awaitingHydrationRef.current = true;
      return;
    }
    const resolved = resolveFloorPlan({
      visualizationDoc,
      legacyBusiness,
    });
    const nextPlan =
      catalogStatus === 'ready'
        ? applyCatalogTruthToPlan(resolved, catalogSections, catalogTables)
        : resolved;
    const incomingJson = JSON.stringify(nextPlan);

    const applyPersisted = (clearDirty) => {
      const cloned = deepCloneFloorPlan(nextPlan);
      planRef.current = cloned;
      setPersistedPlan(deepCloneFloorPlan(nextPlan));
      setPlan(cloned);
      if (clearDirty) {
        dirtyRef.current = false;
        setIsDirty(false);
        setUndoStack([]);
        setRedoStack([]);
      }
    };

    if (awaitingHydrationRef.current) {
      awaitingHydrationRef.current = false;
      pendingSaveJsonRef.current = null;
      applyPersisted(true);
      return;
    }

    if (pendingSaveJsonRef.current) {
      if (incomingJson === pendingSaveJsonRef.current) {
        pendingSaveJsonRef.current = null;
      } else {
        // Stale remote snapshot — keep local until save echoes.
        return;
      }
    }

    // While dirty, keep persistedPlan as the last saved baseline so Save can
    // still DELETE catalog sections/tables removed from the plan. Overwriting
    // it with a remote viz echo would skip those catalog deletes.
    if (!dirtyRef.current) {
      setPersistedPlan(deepCloneFloorPlan(nextPlan));
      if (JSON.stringify(planRef.current) === incomingJson) {
        return;
      }
      applyPersisted(false);
    }
  }, [
    legacyBusiness,
    visualizationDoc,
    loadingBusiness,
    loadingVisualization,
    catalogStatus,
    catalogSections,
    catalogTables,
  ]);


  const catalogTableUuids = useMemo(
    () =>
      new Set(
        (catalogTables || [])
          .map((table) => (table?.tableDetails || table)?.uuid)
          .filter(Boolean)
      ),
    [catalogTables]
  );

  const catalogSectionUuids = useMemo(
    () =>
      new Set(
        (catalogSections || [])
          .map((section) => (section?.sectionDetails || section)?.uuid)
          .filter(Boolean)
      ),
    [catalogSections]
  );

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    setIsDirty(true);
  }, []);

  const pushHistory = useCallback((current) => {
    setUndoStack((prev) => [...prev.slice(-29), deepCloneFloorPlan(current)]);
    setRedoStack([]);
  }, []);

  const snapshotHistory = useCallback(() => {
    pushHistory(planRef.current);
  }, [pushHistory]);

  const mutateSectionLayout = useCallback(
    (sectionUuid, mutate, options = {}) => {
      setPlan((prev) => {
        if (options.recordHistory !== false) {
          pushHistory(prev);
        }
        const withSection = ensureSectionLayout(prev, sectionUuid);
        const section = withSection.sections[sectionUuid];
        const layout = {
          ...section,
          style: { ...section.style },
          canvas: { ...section.canvas },
          outline: normalizeOutline(section.outline).map((point) => ({
            ...point,
          })),
          tables: { ...section.tables },
          objects: { ...(section.objects || {}) },
        };
        mutate(layout);
        const next = {
          ...withSection,
          sections: {
            ...withSection.sections,
            [sectionUuid]: layout,
          },
        };
        planRef.current = next;
        markDirty();
        return next;
      });
    },
    [markDirty, pushHistory]
  );

  // No catalog-sync seed here: the floor plan document is the single source of
  // truth. Tables/sections live in the plan and persist via one PATCH on Save.

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (!stack.length) {
        return stack;
      }
      const previous = stack[stack.length - 1];
      const current = deepCloneFloorPlan(planRef.current);
      setRedoStack((redo) => [...redo.slice(-29), current]);
      planRef.current = previous;
      setPlan(previous);
      markDirty();
      return stack.slice(0, -1);
    });
  }, [markDirty]);

  const redo = useCallback(() => {
    setRedoStack((stack) => {
      if (!stack.length) {
        return stack;
      }
      const next = stack[stack.length - 1];
      const current = deepCloneFloorPlan(planRef.current);
      setUndoStack((undoItems) => [...undoItems.slice(-29), current]);
      planRef.current = next;
      setPlan(next);
      markDirty();
      return stack.slice(0, -1);
    });
  }, [markDirty]);

  const discardAllChanges = useCallback(() => {
    const saved = deepCloneFloorPlan(persistedPlan);
    planRef.current = saved;
    pendingSaveJsonRef.current = null;
    dirtyRef.current = false;
    setPlan(saved);
    setUndoStack([]);
    setRedoStack([]);
    setIsDirty(false);
    setSelectedTableUuid(null);
    setSelectedObjectId(null);
    setSettings(persistedSettings);
    setSettingsDirty(false);
    setDeletedTableUuids([]);
    setDeletedSectionUuids([]);
  }, [persistedPlan, persistedSettings]);

  const flushSave = useCallback(async () => {
    if ((!dirtyRef.current && !settingsDirty) || isSaving) {
      return;
    }
    setIsSaving(true);
    let failedDeletes = [];
    try {
      let planToSave = deepCloneFloorPlan(planRef.current);
      const layoutWasDirty = dirtyRef.current;

      if (layoutWasDirty) {
        // Real sections/tables are created (and removed ones deleted) FIRST.
        // Only once that succeeds do we PATCH the visualization, with any UUID
        // remaps applied. Rows that refused to delete stay in the plan.
        const syncResult = await syncFloorPlanCatalogWithPlan({
          businessUuid,
          plan: planToSave,
          previousPlan: persistedPlan,
          deletedTableUuids,
          deletedSectionUuids,
        });
        planToSave = syncResult.plan;
        failedDeletes = syncResult.failedDeletes || [];
        // Refresh only what catalog sync may have changed. Section-only Saves
        // must not hit GET /cloud-pos/table.
        if (syncResult.touchedSections) {
          dispatch(getSectionPOS(businessUuid));
        }
        if (syncResult.touchedTables) {
          dispatch(getTablePOS(businessUuid));
        }

        const result = await saveFloorPlanToCatalog({
          businessUuid,
          plan: planToSave,
        });
        planToSave = result.snapshot;
        planRef.current = planToSave;
        setPlan(deepCloneFloorPlan(planToSave));
        // Keep in-memory visualization so reload/hydrate prefers the API doc.
        setVisualizationDoc(
          result.doc || {
            version: planToSave.version,
            sections: planToSave.sections || {},
          }
        );
        pendingSaveJsonRef.current = JSON.stringify(planToSave);
        setPersistedPlan(deepCloneFloorPlan(planToSave));
      }

      // Feature toggles (Enabled on POS / Arrange) are business settings, not
      // part of the floor plan doc — only touch business when they changed.
      if (settingsDirty) {
        const business = await getBusinessSettings();
        const body = mergeFloorPlanSettingsIntoBusiness(business, settings);
        await dispatch(updateBusinessSettings(body));
        dispatch({
          type: SET_CURRENT_BUSINESSES,
          payload: {
            ...currentBusiness,
            metadata: body.metadata,
            ...(body.businessDetails
              ? { businessDetails: body.businessDetails }
              : {}),
          },
        });
        setLegacyBusiness(body);
      }

      setPersistedSettings({ ...settings });
      dirtyRef.current = false;
      setIsDirty(false);
      setSettingsDirty(false);
      // Rows that refused to delete were restored to the plan, so nothing stays
      // pending — the user can try again.
      setDeletedTableUuids([]);
      setDeletedSectionUuids([]);

      if (failedDeletes.length) {
        const labels = failedDeletes.map((item) => item.label).join(', ');
        const reason = failedDeletes.find((item) => item.message)?.message;
        toast.warning(
          `Floor plan saved, but could not delete ${labels}${
            reason ? ` — ${reason}` : ''
          }. They were kept on the floor.`
        );
      } else {
        toast.success('Floor plan saved');
      }
    } catch (err) {
      // Creating the real sections/tables failed, so nothing was written to the
      // visualization — the plan stays dirty and the user can retry. Keep the
      // rows that were already created (remapped to their real UUIDs) so the
      // retry updates them instead of creating duplicates.
      if (err?.partialPlan) {
        const partial = deepCloneFloorPlan(err.partialPlan);
        planRef.current = partial;
        setPlan(partial);
        setSelectedTableUuid((prev) =>
          prev && !partial.sections?.[selectedSectionUuid]?.tables?.[prev]
            ? null
            : prev
        );
        dispatch(getSectionPOS(businessUuid));
        dispatch(getTablePOS(businessUuid));
      }
      const reason = readApiErrorMessage(err);
      toast.error(
        reason
          ? `Failed to save floor plan — ${reason}`
          : 'Failed to save floor plan'
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    settingsDirty,
    settings,
    isSaving,
    dispatch,
    currentBusiness,
    businessUuid,
    persistedPlan,
    selectedSectionUuid,
    deletedTableUuids,
    deletedSectionUuids,
  ]);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setSettingsDirty(true);
  }, []);

  const createSection = useCallback(
    (name) => {
      if (!name.trim()) {
        return null;
      }
      const label = name.trim();
      const sectionUuid = uuidv4();
      setPlan((prev) => {
        pushHistory(prev);
        const withSection = ensureSectionLayout(prev, sectionUuid);
        const layout = {
          ...withSection.sections[sectionUuid],
          name: label,
          displayName: label,
        };
        const next = {
          ...withSection,
          sections: { ...withSection.sections, [sectionUuid]: layout },
        };
        planRef.current = next;
        markDirty();
        return next;
      });
      setSelectedSectionUuid(sectionUuid);
      return sectionUuid;
    },
    [markDirty, pushHistory]
  );

  const renameSection = useCallback(
    (sectionUuid, name) => {
      if (!name.trim() || !sectionUuid) {
        return;
      }
      const label = name.trim();
      setPlan((prev) => {
        if (!prev.sections[sectionUuid]) {
          return prev;
        }
        pushHistory(prev);
        const layout = {
          ...prev.sections[sectionUuid],
          name: label,
          displayName: label,
        };
        const next = {
          ...prev,
          sections: { ...prev.sections, [sectionUuid]: layout },
        };
        planRef.current = next;
        markDirty();
        return next;
      });
    },
    [markDirty, pushHistory]
  );

  const removeSection = useCallback(
    (sectionUuid) => {
      const remaining = Object.keys(planRef.current.sections || {}).filter(
        (uuid) => uuid !== sectionUuid
      );
      // Only real rows need deleting on Save; drafts just disappear.
      if (catalogSectionUuids.has(sectionUuid)) {
        setDeletedSectionUuids((prev) =>
          prev.includes(sectionUuid) ? prev : [...prev, sectionUuid]
        );
        markDirty();
      }
      setPlan((prev) => {
        pushHistory(prev);
        const nextSections = { ...prev.sections };
        delete nextSections[sectionUuid];
        const next = { ...prev, sections: nextSections };
        planRef.current = next;
        markDirty();
        return next;
      });
      if (selectedSectionUuid === sectionUuid) {
        setSelectedSectionUuid(remaining[0] || null);
      }
      setSelectedTableUuid(null);
      return null;
    },
    [markDirty, pushHistory, selectedSectionUuid, catalogSectionUuids]
  );

  const updateHallStyle = useCallback(
    // `options.recordHistory: false` lets a live drag (colour picker) stream
    // updates without pushing an undo entry per pointer move.
    (patch, options) => {
      if (!selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(
        selectedSectionUuid,
        (layout) => {
          layout.style = { ...layout.style, ...patch };
        },
        options
      );
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const updateSectionOutline = useCallback(
    (outline) => {
      if (!selectedSectionUuid) {
        return;
      }
      const next = normalizeOutline(outline);
      if (next.length < 3) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        layout.outline = next.map((point) => ({ ...point }));
      });
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const relocateItemsInsideOutline = useCallback(() => {
    if (!selectedSectionUuid) {
      return 0;
    }
    let moved = 0;
    mutateSectionLayout(selectedSectionUuid, (layout) => {
      const outline = normalizeOutline(layout.outline);
      const canvasW = layout.canvas.width;
      const canvasH = layout.canvas.height;
      const grid = layout.style.gridSize || 20;

      Object.entries(layout.tables).forEach(([uuid, table]) => {
        const center = rectCenterNormalized(
          table.x,
          table.y,
          table.width,
          table.height,
          canvasW,
          canvasH
        );
        if (pointInPolygon(center, outline)) {
          return;
        }
        const nextCenter = projectPointInsidePolygon(center, outline);
        layout.tables[uuid] = {
          ...table,
          x: snapToGrid(
            Math.max(
              0,
              Math.min(
                canvasW - table.width,
                nextCenter.x * canvasW - table.width / 2
              )
            ),
            grid
          ),
          y: snapToGrid(
            Math.max(
              0,
              Math.min(
                canvasH - table.height,
                nextCenter.y * canvasH - table.height / 2
              )
            ),
            grid
          ),
        };
        moved += 1;
      });

      Object.entries(layout.objects || {}).forEach(([id, object]) => {
        const center = rectCenterNormalized(
          object.x,
          object.y,
          object.width,
          object.height,
          canvasW,
          canvasH
        );
        if (pointInPolygon(center, outline)) {
          return;
        }
        const nextCenter = projectPointInsidePolygon(center, outline);
        layout.objects[id] = {
          ...object,
          x: snapToGrid(
            Math.max(
              0,
              Math.min(
                canvasW - object.width,
                nextCenter.x * canvasW - object.width / 2
              )
            ),
            grid
          ),
          y: snapToGrid(
            Math.max(
              0,
              Math.min(
                canvasH - object.height,
                nextCenter.y * canvasH - object.height / 2
              )
            ),
            grid
          ),
        };
        moved += 1;
      });
    });
    return moved;
  }, [selectedSectionUuid, mutateSectionLayout]);

  const applySectionShapePreset = useCallback(
    (presetId) => {
      const preset = SECTION_SHAPE_PRESETS.find((item) => item.id === presetId);
      if (!preset || !selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        layout.outline = normalizeOutline(preset.outline).map((point) => ({
          ...point,
        }));
        const outline = layout.outline;
        const canvasW = layout.canvas.width;
        const canvasH = layout.canvas.height;
        const grid = layout.style.gridSize || 20;

        const relocate = (item) => {
          const center = rectCenterNormalized(
            item.x,
            item.y,
            item.width,
            item.height,
            canvasW,
            canvasH
          );
          if (pointInPolygon(center, outline)) {
            return item;
          }
          const nextCenter = projectPointInsidePolygon(center, outline);
          return {
            ...item,
            x: snapToGrid(
              Math.max(
                0,
                Math.min(
                  canvasW - item.width,
                  nextCenter.x * canvasW - item.width / 2
                )
              ),
              grid
            ),
            y: snapToGrid(
              Math.max(
                0,
                Math.min(
                  canvasH - item.height,
                  nextCenter.y * canvasH - item.height / 2
                )
              ),
              grid
            ),
          };
        };

        Object.entries(layout.tables).forEach(([uuid, table]) => {
          layout.tables[uuid] = relocate(table);
        });
        Object.entries(layout.objects || {}).forEach(([id, object]) => {
          layout.objects[id] = relocate(object);
        });
      });
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const updateCanvasSize = useCallback(
    (width, height, options = {}) => {
      if (!selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(
        selectedSectionUuid,
        (layout) => {
          const nextW = options.rescale
            ? Math.max(200, Math.round(width))
            : Math.min(
                MAX_CANVAS_WIDTH,
                Math.max(MIN_CANVAS_WIDTH, Math.round(width))
              );
          const nextH = options.rescale
            ? Math.max(160, Math.round(height))
            : Math.min(
                MAX_CANVAS_HEIGHT,
                Math.max(MIN_CANVAS_HEIGHT, Math.round(height))
              );
          const prevW = Math.max(1, layout.canvas.width);
          const prevH = Math.max(1, layout.canvas.height);
          const sx = options.rescale ? nextW / prevW : 1;
          const sy = options.rescale ? nextH / prevH : 1;

          layout.canvas = { width: nextW, height: nextH };
          if (!layout.outline || layout.outline.length < 3) {
            layout.outline = RECT_OUTLINE.map((point) => ({ ...point }));
          }

          Object.keys(layout.tables).forEach((tableUuid) => {
            const table = layout.tables[tableUuid];
            layout.tables[tableUuid] = {
              ...table,
              x: Math.max(0, Math.min(table.x * sx, nextW - table.width)),
              y: Math.max(0, Math.min(table.y * sy, nextH - table.height)),
            };
          });
          Object.keys(layout.objects || {}).forEach((objectId) => {
            const object = layout.objects[objectId];
            layout.objects[objectId] = {
              ...object,
              x: Math.max(0, Math.min(object.x * sx, nextW - object.width)),
              y: Math.max(0, Math.min(object.y * sy, nextH - object.height)),
            };
          });
        },
        { recordHistory: options.recordHistory }
      );
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const addFloorObject = useCallback(
    (preset) => {
      if (!selectedSectionUuid) {
        return null;
      }
      const layout = planRef.current.sections[selectedSectionUuid];
      const count = Object.keys(layout?.objects || {}).length;
      const gridSize = layout?.style.gridSize ?? 20;
      const outline = normalizeOutline(layout?.outline);
      const occupied = [
        ...Object.values(layout?.tables || {}),
        ...Object.values(layout?.objects || {}),
      ];
      const spot = findSpotInsideOutline(
        preset.width,
        preset.height,
        outline,
        layout?.canvas.width || 1200,
        layout?.canvas.height || 800,
        occupied
      );
      const object = createFloorObject(
        preset,
        snapToGrid(spot.x, gridSize),
        snapToGrid(spot.y, gridSize),
        count + 1
      );
      mutateSectionLayout(selectedSectionUuid, (sectionLayout) => {
        sectionLayout.objects = {
          ...(sectionLayout.objects || {}),
          [object.id]: object,
        };
      });
      setSelectedObjectId(object.id);
      setSelectedTableUuid(null);
      return object.id;
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const updateFloorObject = useCallback(
    (objectId, patch, options = {}) => {
      if (!selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(
        selectedSectionUuid,
        (layout) => {
          const current = layout.objects?.[objectId];
          if (!current) {
            return;
          }
          const gridSize = layout.style.gridSize;
          const next = { ...current, ...patch };
          if (options.snap !== false) {
            if (patch.x !== undefined) {
              next.x = snapToGrid(next.x, gridSize);
            }
            if (patch.y !== undefined) {
              next.y = snapToGrid(next.y, gridSize);
            }
            if (patch.width !== undefined) {
              next.width = snapToGrid(Math.max(24, next.width), gridSize);
            }
            if (patch.height !== undefined) {
              next.height = snapToGrid(Math.max(24, next.height), gridSize);
            }
          }
          const clamped = clampPlacementInsideOutline(
            next.x,
            next.y,
            next.width,
            next.height,
            normalizeOutline(layout.outline),
            layout.canvas.width,
            layout.canvas.height
          );
          next.x = clamped.x;
          next.y = clamped.y;
          layout.objects[objectId] = next;
        },
        { recordHistory: options.recordHistory }
      );
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const removeFloorObject = useCallback(
    (objectId) => {
      if (!selectedSectionUuid || !objectId) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        const next = {};
        Object.entries(layout.objects || {}).forEach(([key, object]) => {
          if (key !== objectId && object.id !== objectId) {
            next[key] = object;
          }
        });
        layout.objects = next;
      });
      setSelectedObjectId(null);
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  // Collect taken table names from the plan + catalog, so `T{n}` stays unique
  // across bulk adds even when some tables exist only in /cloud-pos/table.
  const getTakenTableNames = useCallback(() => {
    const taken = new Set();
    Object.values(planRef.current.sections || {}).forEach((section) => {
      Object.values(section.tables || {}).forEach((placement) => {
        const name = (placement?.name || '').trim();
        const displayName = (placement?.displayName || '').trim();
        if (name) {
          taken.add(name.toLowerCase());
        }
        if (displayName) {
          taken.add(displayName.toLowerCase());
        }
      });
    });
    (catalogTables || []).forEach((table) => {
      const name = (table?.tableDetails?.name || '').trim();
      const displayName = (table?.tableDetails?.displayName || '').trim();
      if (name) {
        taken.add(name.toLowerCase());
      }
      if (displayName) {
        taken.add(displayName.toLowerCase());
      }
    });
    return taken;
  }, [catalogTables]);

  const addTableFromPreset = useCallback(
    // In-memory only: adds a table (identity + placement) to the plan. Persisted
    // with the whole floor plan via one table-visualization PATCH on Save.
    (preset) => {
      if (!selectedSectionUuid) {
        return null;
      }
      const name = allocateNextTableName(getTakenTableNames());
      const tableUuid = uuidv4();

      const layout = planRef.current.sections[selectedSectionUuid];
      const gridSize = layout?.style.gridSize ?? 20;
      const outline = normalizeOutline(layout?.outline);
      const canvasW = layout?.canvas.width || 1200;
      const canvasH = layout?.canvas.height || 800;
      const existingCount = Object.keys(layout?.tables || {}).length;
      const sized = suggestTableSizeForHall(
        preset.width,
        preset.height,
        canvasW,
        canvasH,
        existingCount + 1
      );
      const occupied = [
        ...Object.values(layout?.tables || {}),
        ...Object.values(layout?.objects || {}),
      ];
      const spot = findSpotInsideOutline(
        sized.width,
        sized.height,
        outline,
        canvasW,
        canvasH,
        occupied
      );
      const placement = createDefaultTablePlacement(
        preset.shape,
        sized.width,
        sized.height,
        snapToGrid(spot.x, gridSize),
        snapToGrid(spot.y, gridSize),
        existingCount + 1
      );
      mutateSectionLayout(selectedSectionUuid, (sectionLayout) => {
        sectionLayout.tables[tableUuid] = {
          ...placement,
          name,
          displayName: name,
          capacity: preset.capacity,
        };
      });
      setSelectedTableUuid(tableUuid);
      setSelectedObjectId(null);
      return tableUuid;
    },
    [selectedSectionUuid, getTakenTableNames, mutateSectionLayout]
  );

  const addTablesFromPreset = useCallback(
    // Bulk add is in-memory only: creates N tables (identity + placement) in the
    // plan and arranges them. Everything persists via one PATCH on Save.
    (preset, count) => {
      if (!selectedSectionUuid) {
        return null;
      }
      const total = Math.min(40, Math.max(1, Math.round(count)));
      const existingInSection = Object.keys(
        planRef.current.sections[selectedSectionUuid]?.tables || {}
      ).length;
      const takenNames = getTakenTableNames();

      const created = [];
      for (let i = 0; i < total; i += 1) {
        created.push({
          uuid: uuidv4(),
          name: allocateNextTableName(takenNames),
        });
      }
      const createdUuids = created.map((item) => item.uuid);
      const nameByUuid = new Map(
        created.map((item) => [item.uuid, item.name])
      );

      if (!createdUuids.length) {
        return null;
      }

      mutateSectionLayout(selectedSectionUuid, (layout) => {
        const gridSize = layout.style.gridSize;
        const outline = normalizeOutline(layout.outline);
        const totalAfter = existingInSection + createdUuids.length;
        const sized = suggestTableSizeForHall(
          preset.width,
          preset.height,
          layout.canvas.width,
          layout.canvas.height,
          totalAfter
        );
        const occupied = [
          ...Object.values(layout.tables),
          ...Object.values(layout.objects || {}),
        ];
        createdUuids.forEach((uuid, index) => {
          const spot = findSpotInsideOutline(
            sized.width,
            sized.height,
            outline,
            layout.canvas.width,
            layout.canvas.height,
            occupied
          );
          const tableName = nameByUuid.get(uuid);
          const placement = {
            ...createDefaultTablePlacement(
              preset.shape,
              sized.width,
              sized.height,
              snapToGrid(spot.x, gridSize),
              snapToGrid(spot.y, gridSize),
              existingInSection + index + 1
            ),
            name: tableName,
            displayName: tableName,
            capacity: preset.capacity,
          };
          layout.tables[uuid] = placement;
          occupied.push(placement);
        });

        const entries = Object.entries(layout.tables);
        entries.forEach(([uuid, placement]) => {
          const fit = suggestTableSizeForHall(
            placement.width,
            placement.height,
            layout.canvas.width,
            layout.canvas.height,
            entries.length
          );
          layout.tables[uuid] = {
            ...placement,
            width: fit.width,
            height: fit.height,
          };
        });

        const arranged = arrangeTablesInHall(
          entries.map(([uuid]) => ({
            uuid,
            width: layout.tables[uuid].width,
            height: layout.tables[uuid].height,
          })),
          outline,
          layout.canvas.width,
          layout.canvas.height
        );
        if (arranged.canvas) {
          layout.canvas = {
            width: arranged.canvas.width,
            height: arranged.canvas.height,
          };
        }
        Object.entries(arranged.placements).forEach(([uuid, pose]) => {
          const current = layout.tables[uuid];
          if (!current) {
            return;
          }
          layout.tables[uuid] = {
            ...current,
            x: snapToGrid(pose.x, gridSize),
            y: snapToGrid(pose.y, gridSize),
            width: pose.width,
            height: pose.height,
            rotation: pose.rotation,
          };
        });
      });

      setSelectedTableUuid(null);
      setSelectedObjectId(null);
      return createdUuids[createdUuids.length - 1] || null;
    },
    [selectedSectionUuid, getTakenTableNames, mutateSectionLayout]
  );

  const applyArrangement = useCallback(
    (kind) => {
      if (!selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        const entries = Object.entries(layout.tables);
        if (entries.length < 2) {
          return;
        }
        const outline = normalizeOutline(layout.outline);
        const arranged = computeArrangement(
          kind,
          entries.map(([uuid, placement]) => ({
            uuid,
            width: placement.width,
            height: placement.height,
          })),
          layout.canvas.width,
          layout.canvas.height
        );
        Object.entries(arranged).forEach(([uuid, pose]) => {
          const current = layout.tables[uuid];
          if (!current) {
            return;
          }
          const clamped = clampPlacementInsideOutline(
            pose.x,
            pose.y,
            current.width,
            current.height,
            outline,
            layout.canvas.width,
            layout.canvas.height
          );
          layout.tables[uuid] = {
            ...current,
            x: snapToGrid(clamped.x, layout.style.gridSize),
            y: snapToGrid(clamped.y, layout.style.gridSize),
            rotation: pose.rotation,
          };
        });
      });
      setSelectedTableUuid(null);
      setSelectedObjectId(null);
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const arrangeTablesInHallShape = useCallback(
    (options = {}) => {
      if (!selectedSectionUuid) {
        return 0;
      }
      let count = 0;
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        const entries = Object.entries(layout.tables);
        count = entries.length;
        if (count === 0) {
          return;
        }
        const outline = normalizeOutline(layout.outline);
        const arranged = arrangeTablesInHall(
          entries.map(([uuid, placement]) => ({
            uuid,
            width: placement.width,
            height: placement.height,
          })),
          outline,
          layout.canvas.width,
          layout.canvas.height,
          36,
          options
        );
        if (arranged.canvas) {
          layout.canvas = {
            width: arranged.canvas.width,
            height: arranged.canvas.height,
          };
        }
        Object.entries(arranged.placements).forEach(([uuid, pose]) => {
          const current = layout.tables[uuid];
          if (!current) {
            return;
          }
          layout.tables[uuid] = {
            ...current,
            x: snapToGrid(pose.x, layout.style.gridSize),
            y: snapToGrid(pose.y, layout.style.gridSize),
            width: pose.width,
            height: pose.height,
            rotation: pose.rotation,
          };
        });
      });
      setSelectedTableUuid(null);
      setSelectedObjectId(null);
      return count;
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const applyLayoutTemplate = useCallback(
    // In-memory quick pack: reuses the section's existing tables and creates the
    // remainder as new plan tables (identity + placement). Persisted via PATCH.
    (templateId) => {
      if (!selectedSectionUuid) {
        return 0;
      }
      const template = LAYOUT_TEMPLATES.find((item) => item.id === templateId);
      if (!template) {
        return 0;
      }

      const layout = planRef.current.sections[selectedSectionUuid];
      const canvasW = layout?.canvas.width ?? 1200;
      const canvasH = layout?.canvas.height ?? 800;
      const existingUuids = Object.keys(layout?.tables || {});

      const takenNames = getTakenTableNames();
      const created = [];
      const needed = Math.max(0, template.slots.length - existingUuids.length);
      for (let i = 0; i < needed; i += 1) {
        const slot = template.slots[existingUuids.length + i];
        const preset = resolvePresetForSlot(slot);
        created.push({
          uuid: uuidv4(),
          name: allocateNextTableName(takenNames),
          capacity: preset.capacity,
        });
      }
      const createdMeta = new Map(created.map((item) => [item.uuid, item]));
      const allUuids = [
        ...existingUuids,
        ...created.map((item) => item.uuid),
      ].slice(0, template.slots.length);

      mutateSectionLayout(selectedSectionUuid, (sectionLayout) => {
        template.slots.forEach((slot, index) => {
          const uuid = allUuids[index];
          if (!uuid) {
            return;
          }
          const base = placementFromTemplateSlot(
            slot,
            sectionLayout.canvas.width || canvasW,
            sectionLayout.canvas.height || canvasH,
            index + 1,
            template.slots.length
          );
          const existing = sectionLayout.tables[uuid];
          const meta = createdMeta.get(uuid);
          const name = existing?.name || meta?.name;
          const displayName =
            existing?.displayName || existing?.name || meta?.name;
          const capacity =
            existing?.capacity !== undefined
              ? existing.capacity
              : meta?.capacity;
          sectionLayout.tables[uuid] = {
            ...base,
            ...(name ? { name } : {}),
            ...(displayName ? { displayName } : {}),
            ...(capacity !== undefined ? { capacity } : {}),
          };
        });
      });

      setSelectedTableUuid(null);
      setSelectedObjectId(null);
      return allUuids.length;
    },
    [selectedSectionUuid, getTakenTableNames, mutateSectionLayout]
  );

  const updateTablePlacement = useCallback(
    (tableUuid, patch, options = {}) => {
      if (!selectedSectionUuid) {
        return;
      }
      const recordHistory = options.recordHistory ?? true;
      mutateSectionLayout(
        selectedSectionUuid,
        (layout) => {
          const current = layout.tables[tableUuid];
          if (!current) {
            return;
          }
          const gridSize = layout.style.gridSize;
          const next = { ...current, ...patch };
          if (options.snap !== false) {
            if (patch.x !== undefined) {
              next.x = snapToGrid(next.x, gridSize);
            }
            if (patch.y !== undefined) {
              next.y = snapToGrid(next.y, gridSize);
            }
            if (patch.width !== undefined) {
              next.width = snapToGrid(clampTableSize(next.width), gridSize);
            }
            if (patch.height !== undefined) {
              next.height = snapToGrid(clampTableSize(next.height), gridSize);
            }
          } else {
            if (patch.width !== undefined) {
              next.width = clampTableSize(next.width);
            }
            if (patch.height !== undefined) {
              next.height = clampTableSize(next.height);
            }
          }
          const clamped = clampPlacementInsideOutline(
            next.x,
            next.y,
            next.width,
            next.height,
            normalizeOutline(layout.outline),
            layout.canvas.width,
            layout.canvas.height
          );
          next.x = clamped.x;
          next.y = clamped.y;
          layout.tables[tableUuid] = next;
        },
        { recordHistory }
      );
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const updateTableCatalog = useCallback(
    // In-memory: updates the table's embedded identity in the plan. A section
    // change is handled by moveTableToSection. Persisted via one PATCH on Save.
    (tableUuid, patch) => {
      if (!tableUuid || !selectedSectionUuid) {
        return;
      }
      if (
        patch.sectionUuid !== undefined &&
        patch.sectionUuid !== selectedSectionUuid
      ) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        const current = layout.tables[tableUuid];
        if (!current) {
          return;
        }
        layout.tables[tableUuid] = {
          ...current,
          ...(patch.name !== undefined ? { name: patch.name } : {}),
          ...(patch.displayName !== undefined
            ? { displayName: patch.displayName }
            : {}),
          ...(patch.capacity !== undefined
            ? { capacity: Number(patch.capacity) || 0 }
            : {}),
        };
      });
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const moveTableToSection = useCallback(
    (tableUuid, targetSectionUuid) => {
      if (!selectedSectionUuid || selectedSectionUuid === targetSectionUuid) {
        return;
      }
      const currentPlacement =
        planRef.current.sections[selectedSectionUuid]?.tables[tableUuid];

      setPlan((prev) => {
        pushHistory(prev);
        const from = ensureSectionLayout(prev, selectedSectionUuid);
        const to = ensureSectionLayout(from, targetSectionUuid);
        const placement =
          currentPlacement ||
          createDefaultTablePlacement('square', 120, 120, 40, 40, 1);
        const fromTables = { ...to.sections[selectedSectionUuid].tables };
        delete fromTables[tableUuid];
        const toTables = {
          ...to.sections[targetSectionUuid].tables,
          [tableUuid]: placement,
        };
        const next = {
          ...to,
          sections: {
            ...to.sections,
            [selectedSectionUuid]: {
              ...to.sections[selectedSectionUuid],
              tables: fromTables,
            },
            [targetSectionUuid]: {
              ...to.sections[targetSectionUuid],
              tables: toTables,
            },
          },
        };
        planRef.current = next;
        markDirty();
        return next;
      });
      setSelectedSectionUuid(targetSectionUuid);
      setSelectedTableUuid(tableUuid);
    },
    [selectedSectionUuid, markDirty, pushHistory]
  );

  const removeTable = useCallback(
    // Deletes the table itself: the placement goes now, the real row goes on
    // Save. Use removeFromFloor to only unplace it.
    (tableUuid) => {
      if (catalogTableUuids.has(tableUuid)) {
        setDeletedTableUuids((prev) =>
          prev.includes(tableUuid) ? prev : [...prev, tableUuid]
        );
        markDirty();
      }
      if (selectedSectionUuid) {
        mutateSectionLayout(selectedSectionUuid, (layout) => {
          delete layout.tables[tableUuid];
        });
      }
      if (selectedTableUuid === tableUuid) {
        setSelectedTableUuid(null);
      }
      return null;
    },
    [
      selectedSectionUuid,
      mutateSectionLayout,
      selectedTableUuid,
      catalogTableUuids,
      markDirty,
    ]
  );

  const clearAllTables = useCallback(() => {
    if (!selectedSectionUuid) {
      return 'Select a section first.';
    }
    // Every real table of this section is deleted on Save, placed or not.
    const realUuids = (catalogTables || [])
      .map((table) => table?.tableDetails || table)
      .filter((details) => details?.sectionUuid === selectedSectionUuid)
      .map((details) => details.uuid)
      .filter(Boolean);
    if (realUuids.length) {
      setDeletedTableUuids((prev) =>
        Array.from(new Set([...prev, ...realUuids]))
      );
      markDirty();
    }
    mutateSectionLayout(selectedSectionUuid, (layout) => {
      layout.tables = {};
    });
    setSelectedTableUuid(null);
    setSelectedObjectId(null);
    return null;
  }, [selectedSectionUuid, mutateSectionLayout, catalogTables, markDirty]);

  const duplicateSelectedTable = useCallback(
    // In-memory duplicate (identity + placement). Persisted via one PATCH.
    (sourceUuidArg) => {
      const sourceUuid = sourceUuidArg || selectedTableUuid;
      if (!selectedSectionUuid || !sourceUuid) {
        return null;
      }
      const placement =
        planRef.current.sections[selectedSectionUuid]?.tables[sourceUuid];
      if (!placement) {
        return null;
      }
      const name = allocateUniqueCopyName(
        getTakenTableNames(),
        placement.displayName || placement.name || 'T'
      );
      const gridSizeDup =
        planRef.current.sections[selectedSectionUuid]?.style.gridSize ?? 20;
      const dupUuid = uuidv4();

      mutateSectionLayout(selectedSectionUuid, (layout) => {
        layout.tables[dupUuid] = {
          ...placement,
          name,
          displayName: name,
          x: snapToGrid(placement.x + gridSizeDup * 2, gridSizeDup),
          y: snapToGrid(placement.y + gridSizeDup * 2, gridSizeDup),
          zIndex: Object.keys(layout.tables).length + 1,
        };
      });
      setSelectedTableUuid(dupUuid);
      setSelectedObjectId(null);
      return dupUuid;
    },
    [
      selectedSectionUuid,
      selectedTableUuid,
      getTakenTableNames,
      mutateSectionLayout,
    ]
  );

  const rotateSelectedTable = useCallback(() => {
    if (!selectedSectionUuid || !selectedTableUuid) {
      return;
    }
    mutateSectionLayout(selectedSectionUuid, (layout) => {
      const current = layout.tables[selectedTableUuid];
      if (!current) {
        return;
      }
      const nextRotation = (current.rotation + 90) % 360;
      const shouldSwap = nextRotation % 180 !== current.rotation % 180;
      layout.tables[selectedTableUuid] = {
        ...current,
        rotation: nextRotation,
        width: shouldSwap ? current.height : current.width,
        height: shouldSwap ? current.width : current.height,
      };
    });
  }, [selectedSectionUuid, selectedTableUuid, mutateSectionLayout]);

  const placeCatalogTable = useCallback(
    (table) => {
      const uuid = table?.tableDetails?.uuid;
      if (!uuid || !selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        if (layout.tables[uuid]) {
          return;
        }
        const preset = resolvePresetForCapacity(table.tableDetails?.capacity);
        const sized = suggestTableSizeForHall(
          preset.width,
          preset.height,
          layout.canvas.width,
          layout.canvas.height,
          Object.keys(layout.tables).length + 1
        );
        const occupied = [
          ...Object.values(layout.tables),
          ...Object.values(layout.objects || {}),
        ];
        const spot = findSpotInsideOutline(
          sized.width,
          sized.height,
          normalizeOutline(layout.outline),
          layout.canvas.width,
          layout.canvas.height,
          occupied
        );
        const gridSize = layout.style.gridSize ?? 20;
        const details = table.tableDetails || {};
        const name = details.name || details.displayName || '';
        layout.tables[uuid] = {
          ...createDefaultTablePlacement(
            preset.shape,
            sized.width,
            sized.height,
            snapToGrid(spot.x, gridSize),
            snapToGrid(spot.y, gridSize),
            Object.keys(layout.tables).length + 1
          ),
          ...(name ? { name } : {}),
          ...(details.displayName || name
            ? { displayName: details.displayName || name }
            : {}),
          ...(Number.isFinite(Number(details.capacity))
            ? { capacity: Number(details.capacity) }
            : {}),
        };
      });
      setSelectedTableUuid(uuid);
      setSelectedObjectId(null);
    },
    [selectedSectionUuid, mutateSectionLayout]
  );

  const removeFromFloor = useCallback(
    (tableUuid) => {
      if (!selectedSectionUuid) {
        return;
      }
      mutateSectionLayout(selectedSectionUuid, (layout) => {
        delete layout.tables[tableUuid];
      });
      if (selectedTableUuid === tableUuid) {
        setSelectedTableUuid(null);
      }
    },
    [selectedSectionUuid, mutateSectionLayout, selectedTableUuid]
  );

  const selectTable = useCallback((uuid) => {
    setSelectedTableUuid(uuid);
    if (uuid) {
      setSelectedObjectId(null);
    }
  }, []);

  const selectObject = useCallback((id) => {
    setSelectedObjectId(id);
    if (id) {
      setSelectedTableUuid(null);
    }
  }, []);

  const sectionLayout = useMemo(() => {
    if (!selectedSectionUuid) {
      return null;
    }
    return (
      ensureSectionLayout(plan, selectedSectionUuid).sections[
        selectedSectionUuid
      ] || null
    );
  }, [plan, selectedSectionUuid]);

  // Sections + tables come from the visualization document only.
  // Real sections first (catalog = source of truth), then sections added in the
  // builder that have not been created yet.
  const sections = useMemo(() => {
    const list = [];
    const seen = new Set();
    (catalogSections || []).forEach((section) => {
      const details = section?.sectionDetails || section;
      const uuid = details?.uuid;
      if (!uuid || seen.has(uuid) || deletedSectionUuids.includes(uuid)) {
        return;
      }
      seen.add(uuid);
      const label = details.displayName || details.name || 'Section';
      list.push({ sectionDetails: { uuid, name: label, displayName: label } });
    });
    Object.entries(plan.sections || {}).forEach(([uuid, section]) => {
      if (seen.has(uuid) || deletedSectionUuids.includes(uuid)) {
        return;
      }
      seen.add(uuid);
      const label = section?.displayName || section?.name || 'Section';
      list.push({ sectionDetails: { uuid, name: label, displayName: label } });
    });
    return list;
  }, [catalogSections, plan, deletedSectionUuids]);

  // Real tables of the section (placed or not), plus tables added in the builder
  // that have not been created yet. Placement identity wins so edits made here
  // show immediately; hydration refreshes it back from the catalog.
  const sectionTables = useMemo(() => {
    if (!selectedSectionUuid) {
      return [];
    }
    const layout = plan.sections?.[selectedSectionUuid];
    const list = [];
    const seen = new Set();

    const push = (uuid, placement, details) => {
      const name =
        placement?.name || details?.name || details?.displayName || uuid;
      const displayName =
        placement?.displayName ||
        placement?.name ||
        details?.displayName ||
        details?.name ||
        name;
      const capacity =
        placement?.capacity !== undefined
          ? placement.capacity
          : Number(details?.capacity) || 0;
      list.push({
        tableDetails: {
          uuid,
          sectionUuid: selectedSectionUuid,
          name,
          displayName,
          capacity,
          shape: placement?.shape,
        },
      });
    };

    (catalogTables || []).forEach((table) => {
      const details = table?.tableDetails || table;
      const uuid = details?.uuid;
      if (
        !uuid ||
        details.sectionUuid !== selectedSectionUuid ||
        deletedTableUuids.includes(uuid)
      ) {
        return;
      }
      seen.add(uuid);
      push(uuid, layout?.tables?.[uuid], details);
    });

    Object.entries(layout?.tables || {}).forEach(([uuid, placement]) => {
      if (seen.has(uuid) || deletedTableUuids.includes(uuid)) {
        return;
      }
      push(uuid, placement, null);
    });

    return list;
  }, [plan, selectedSectionUuid, catalogTables, deletedTableUuids]);

  useEffect(() => {
    const uuids = sections
      .map((section) => section?.sectionDetails?.uuid)
      .filter(Boolean);
    if (!uuids.length) {
      setSelectedSectionUuid(null);
      return;
    }
    setSelectedSectionUuid((prev) =>
      prev && uuids.includes(prev) ? prev : uuids[0]
    );
  }, [sections]);

  const isBusy =
    loadingBusiness || loadingVisualization || catalogStatus === 'loading';

  return {
    plan,
    settings,
    updateSettings,
    sectionLayout,
    sectionTables,
    sections,
    selectedSectionUuid,
    selectedTableUuid,
    selectedObjectId,
    isSaving,
    isDirty: isDirty || settingsDirty,
    isBusy,
    setSelectedSectionUuid,
    setSelectedTableUuid: selectTable,
    setSelectedObjectId: selectObject,
    createSection,
    renameSection,
    removeSection,
    updateHallStyle,
    updateSectionOutline,
    applySectionShapePreset,
    relocateItemsInsideOutline,
    updateCanvasSize,
    addTableFromPreset,
    addTablesFromPreset,
    addFloorObject,
    updateFloorObject,
    removeFloorObject,
    applyArrangement,
    arrangeTablesInHallShape,
    applyLayoutTemplate,
    updateTablePlacement,
    updateTableCatalog,
    moveTableToSection,
    removeTable,
    clearAllTables,
    duplicateSelectedTable,
    rotateSelectedTable,
    placeCatalogTable,
    removeFromFloor,
    snapshotHistory,
    flushSave,
    discardAllChanges,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    canDiscardAll: isDirty || settingsDirty || undoStack.length > 0,
    presets: TABLE_SHAPE_PRESETS,
    objectPresets: FLOOR_OBJECT_PRESETS,
    layoutTemplates: LAYOUT_TEMPLATES,
  };
};

export default useFloorPlanBuilder;
